/**
 * Carte interactive des vignobles (Leaflet). Combine une carte réelle (tuiles) et la
 * carte illustrée fournie posée en superposition géoréférencée sur la bbox France, afin
 * que les points GPS des domaines tombent au bon endroit. Les points sont stylés selon leur
 * statut, déplaçables (correction manuelle persistée) et gardent une taille constante à l'écran.
 * Réutilisée par l'écran Vignobles et le volet « Voir sur la carte » de la fiche cuvée.
 */
import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { WINE_REGIONS } from './wineRegions';

const ACCENT = '#7d5411';
const NEUTRAL = '#605d5d';

/** Vrai si v est une paire [lat, lng] de nombres valides (protège Leaflet des coords invalides). */
function isLatLng(v) {
  return Array.isArray(v) && v.length === 2 && v.every((n) => typeof n === 'number' && !Number.isNaN(n));
}

/** Interpolation Catmull-Rom d'un polygone fermé → courbe lissée (sans angles droits). */
function smoothClosed(pts, seg = 14) {
  const n = pts.length; const out = [];
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n], p1 = pts[i], p2 = pts[(i + 1) % n], p3 = pts[(i + 2) % n];
    for (let t = 0; t < seg; t++) {
      const s = t / seg, s2 = s * s, s3 = s2 * s;
      const lat = 0.5 * ((2 * p1[0]) + (-p0[0] + p2[0]) * s + (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * s2 + (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * s3);
      const lng = 0.5 * ((2 * p1[1]) + (-p0[1] + p2[1]) * s + (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * s2 + (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * s3);
      out.push([lat, lng]);
    }
  }
  return out;
}

/** Version foncée d'une couleur hex (pour le libellé lisible sur la zone). */
function darken(hex, f = 0.62) {
  const n = parseInt(hex.slice(1), 16);
  return `rgb(${Math.round(((n >> 16) & 255) * f)},${Math.round(((n >> 8) & 255) * f)},${Math.round((n & 255) * f)})`;
}

// Bbox France métropolitaine (+ Corse) pour cadrer la vue initiale.
export const FRANCE_BOUNDS = [[41.2, -5.3], [51.2, 9.7]];

/** HTML d'un point selon le statut (rond plein / cercle vide / pointillés / losange). */
function pointHtml(status, selected) {
  const ring = selected ? `box-shadow:0 0 0 3px rgba(125,84,17,0.25);` : '';
  const size = selected ? 16 : 12;
  const base = `width:${size}px;height:${size}px;border-radius:50%;${ring}`;
  if (status === 'cave') return `<div style="${base}background:${ACCENT};border:2px solid #f3f2f2"></div>`;
  if (status === 'deguste') return `<div style="${base}background:transparent;border:2px solid ${ACCENT}"></div>`;
  return `<div style="${base}background:transparent;border:2px dashed ${NEUTRAL}"></div>`;
}

export default function WineMap({ domains = [], center, zoom = 6, selectedId, onSelect, onMove, home, regions = true }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: true, attributionControl: false, minZoom: 5, maxZoom: 13 });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 18, opacity: 0.9 }).addTo(map);
    if (regions) {
      // Zones viticoles lissées (courbes) + libellé en petites capitales dans la couleur de la région.
      WINE_REGIONS.forEach((r) => {
        L.polygon(smoothClosed(r.coords), {
          color: r.color, weight: 1, opacity: 0.9, fillColor: r.color, fillOpacity: 0.35,
          lineJoin: 'round', interactive: false,
        }).addTo(map);
        L.marker(r.label, {
          interactive: false,
          icon: L.divIcon({
            className: '', iconSize: [150, 16], iconAnchor: [75, 8],
            html: `<div style="text-align:center;font-family:'IBM Plex Sans',sans-serif;font-size:10.5px;font-weight:600;font-variant:small-caps;text-transform:lowercase;letter-spacing:0.12em;color:${darken(r.color)};text-shadow:0 0 3px rgba(243,242,242,0.95),0 1px 2px rgba(243,242,242,0.95)">${r.name}</div>`,
          }),
        }).addTo(map);
      });
    }
    map.fitBounds(FRANCE_BOUNDS);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapRef.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const map = mapRef.current;
    const group = layerRef.current;
    if (!map || !group) return;
    group.clearLayers();

    if (isLatLng(home)) {
      L.marker(home, { icon: L.divIcon({ className: '', iconSize: [16, 16], iconAnchor: [8, 8],
        html: '<div style="width:14px;height:14px;background:#201f1d;transform:rotate(45deg);border:2px solid #f3f2f2"></div>' }),
      }).addTo(group).bindTooltip('Maison des Monts Bleus');
    }

    domains.forEach((d) => {
      const pos = d.map_override || d.gps;
      if (!isLatLng(pos)) return;
      const selected = d.id === selectedId;
      const marker = L.marker(pos, {
        draggable: Boolean(onMove),
        icon: L.divIcon({ className: '', iconSize: [18, 18], iconAnchor: [9, 9], html: pointHtml(d.status, selected) }),
      }).addTo(group);
      marker.bindTooltip(`${d.name}${d.region ? ' · ' + d.region : ''}`);
      if (onSelect) marker.on('click', () => onSelect(d.id));
      if (onMove) marker.on('dragend', (e) => { const ll = e.target.getLatLng(); onMove(d.id, { lat: ll.lat, lng: ll.lng }); });
    });
  }, [domains, selectedId, home, onSelect, onMove]);

  useEffect(() => {
    if (mapRef.current && isLatLng(center)) mapRef.current.setView(center, zoom);
  }, [center?.[0], center?.[1], zoom]); // eslint-disable-line react-hooks/exhaustive-deps

  return <div ref={containerRef} className="w-full h-full" data-testid="wine-map" />;
}
